import React, { useState } from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { generateAskAnything } from '../../services/openai';
import UsageLimiter from '../UsageLimiter';
import { useUsage } from '../../contexts/UsageContext';

const responseLengths = [
  { name: 'Short', value: 'short', description: '~250 words (brief and concise)' },
  { name: 'Medium', value: 'medium', description: '~500 words (moderate detail)' },
  { name: 'Long', value: 'long', description: '~1500 words (comprehensive)' }
];

export default function AskAnythingPrompt() {
  const [formData, setFormData] = useState({
    question: '',
    responseLength: 'medium'
  });
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { usageStatus, showAd, checkAndRecordRequest } = useUsage();

  const handleShowAd = () => {
    console.log('handleShowAd clicked'); // Debug log
    showAd();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!checkAndRecordRequest()) {
        throw new Error('Usage limit reached');
      }

      const result = await generateAskAnything(formData);
      setResponse(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(response);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Ask Anything</h1>

        <div className="prose prose-lg text-gray-600 mb-8">
          Ask me anything! Whether you need help brainstorming ideas, crafting a story, solving a problem, or anything else, I'm here to assist.
          <br /><br />
          Here's how to guide your request:
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question/Request Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Your Question or Request: <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none resize-none"
              placeholder="Write what you need help with, e.g., 'Explain quantum mechanics in simple terms' or 'Give me 10 marketing ideas for my small business.'"
              required
            />
          </div>

          {/* Response Length Field */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Preferred Response Length:
            </label>
            <Listbox
              value={formData.responseLength}
              onChange={(value) => setFormData({ ...formData, responseLength: value })}
            >
              <div className="relative">
                <Listbox.Button className="relative w-full py-2 pl-3 pr-10 text-left border-b-2 border-gray-300 focus:outline-none focus:border-primary">
                  <span className="block truncate">
                    {responseLengths.find(length => length.value === formData.responseLength)?.name}: {responseLengths.find(length => length.value === formData.responseLength)?.description}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                  </span>
                </Listbox.Button>

                <Listbox.Options className="absolute z-10 w-full mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {responseLengths.map((length) => (
                    <Listbox.Option
                      key={length.value}
                      value={length.value}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-blue-600 text-white' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {length.name}: {length.description}
                          </span>
                          {selected ? (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                active ? 'text-white' : 'text-blue-600'
                              }`}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between mt-4">
            <button
              type="submit"
              disabled={loading || !formData.question.trim()}
              className="w-full px-4 py-2 text-white bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate Response'
              )}
            </button>
            {usageStatus.allowed && (
              <span className="ml-4 text-sm text-gray-600">
                Requests remaining before ad: {usageStatus.requestsLeft}
              </span>
            )}
          </div>

          {!usageStatus.allowed && usageStatus.reason === 'cooldown' && (
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="text-yellow-700">
                    Usage limit reached. Please wait {Math.ceil(usageStatus.remainingTime / 1000)} seconds or view an ad to help support this site.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleShowAd}
                  className="ml-4 px-4 py-2 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md flex items-center whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  View Ad to Continue
                </button>
              </div>
            </div>
          )}

          {!usageStatus.allowed && usageStatus.reason === 'limit' && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 mb-2">Usage limit reached. Please wait {usageStatus.waitTime} seconds or view an ad to continue.</p>
              <button
                type="button"
                onClick={handleShowAd}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
              >
                View Ad to Continue
              </button>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
              {error}
            </div>
          )}

          {/* Response Area */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Generated Response:</label>
              {response && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-gray-700 bg-white rounded-md hover:bg-gray-100 transition-colors border border-gray-300"
                  title="Copy to clipboard"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                  <span className="text-sm">Copy</span>
                  {copied && (
                    <span className="absolute mt-8 right-0 bg-gray-800 text-white text-xs px-2 py-1 rounded">
                      Copied!
                    </span>
                  )}
                </button>
              )}
            </div>
            <div 
              className="w-full h-[500px] p-4 bg-white border border-gray-300 rounded-md overflow-y-auto"
            >
              <pre className="whitespace-pre-wrap font-sans text-gray-800">
                {response}
              </pre>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
